import { datadogRum } from "@datadog/browser-rum";
import { isEmbedded } from "@commander4/elements/lib/embed-protocol";

/**
 * Initialize Datadog RUM (Real User Monitoring)
 * Monitors frontend performance, errors, and user sessions for JoyIA
 */
export function initDatadog() {
  const applicationId = import.meta.env.VITE_DATADOG_RUM_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN;
  const env = import.meta.env.VITE_DATADOG_ENV || import.meta.env.MODE || "dev";
  const service = import.meta.env.VITE_DATADOG_SERVICE || "joyia-knowledgeflow";
  // UST version, supplied by infra alongside env and service. Without it every RUM
  // session reported no version, so a regression could not be tied to a deploy.
  const version = import.meta.env.VITE_DATADOG_VERSION;

  // Only initialize if credentials are available
  if (!applicationId || !clientToken) {
    console.warn("Datadog RUM credentials not found, skipping initialization");
    return;
  }

  try {
    datadogRum.init({
      applicationId,
      clientToken,
      site: "datadoghq.eu",
      service,
      env,
      ...(version ? { version } : {}),
      sessionSampleRate: 100,
      /*
       * Session Replay is suppressed inside the Joyia Web shell, and only there.
       *
       * This app is embedded as an iframe by `packages/web` (see its
       * `lib/embedded-apps.ts`), and the shell boots several frames at once — the `me`
       * entry mounts `hostPrefix: "/"`, so simply landing on the dashboard starts this
       * app plus `news`. Each one was initialising its OWN replay recorder, which
       * serialises every DOM mutation it sees. A user reported the page crawling and
       * then killing the browser, with three recorders visible in one tab's console.
       *
       * Only the host's recording is ever watched, and it already captures these frames
       * visually, so the extra recorders bought nothing. Error and performance
       * telemetry is untouched; opened standalone in its own tab this app still records
       * normally.
       *
       * `isEmbedded()` is safe at module scope: it reads the `?embedded=1` param the
       * host always appends before falling back to a frame check.
       */
      sessionReplaySampleRate: isEmbedded() ? 0 : 20,
      defaultPrivacyLevel: "mask-user-input",
    });

    console.log(`Datadog RUM initialized for ${service} (${env})`);
  } catch (error) {
    console.error("Failed to initialize Datadog RUM:", error);
  }
}
